import {
  Grid,
  Container,
  Typography,
  Box,
  IconButton,
  makeStyles,
  useTheme,
  useMediaQuery,
} from '@material-ui/core';
import { useNavigate } from 'react-router-dom';
import FacebookIcon from '@material-ui/icons/Facebook';
import YouTubeIcon from '@material-ui/icons/YouTube';
import LinkedInIcon from '@material-ui/icons/LinkedIn';
import InstagramIcon from '@material-ui/icons/Instagram';
import EmailIcon from '@material-ui/icons/Email';
import LocationOnIcon from '@material-ui/icons/LocationOn';

const useStyles = makeStyles((theme) => ({
  footer: {
    background: 'linear-gradient(135deg, #4c004c 0%, #3a003a 100%)',
    color: '#ffffff',
    padding: '48px 0 24px',
    [theme.breakpoints.down('sm')]: {
      padding: '40px 0 20px',
    },
  },
  logoContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 16,
  },
  logo: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    marginRight: 12,
  },
  companyName: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: '#ffffff',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1rem',
    },
  },
  description: {
    fontSize: '0.85rem',
    color: 'rgba(255, 255, 255, 0.8)',
    lineHeight: 1.6,
    marginBottom: 20,
    maxWidth: 280,
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.8rem',
      maxWidth: '100%',
    },
  },
  contactItem: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: 12,
    '& svg': {
      fontSize: 16,
      marginRight: 10,
      marginTop: 2,
      color: 'rgba(255, 255, 255, 0.7)',
    },
  },
  contactText: {
    fontSize: '0.8rem',
    color: 'rgba(255, 255, 255, 0.85)',
    lineHeight: 1.5,
  },
  sectionTitle: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#ffffff',
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 20,
    position: 'relative',
    paddingBottom: 10,
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      width: 24,
      height: 2,
      backgroundColor: 'rgba(255, 255, 255, 0.4)',
    },
    [theme.breakpoints.down('sm')]: {
      marginBottom: 16,
      marginTop: 24,
    },
  },
  linkItem: {
    marginBottom: 10,
  },
  link: {
    fontSize: '0.82rem',
    color: 'rgba(255, 255, 255, 0.8)',
    textDecoration: 'none',
    cursor: 'pointer',
    transition: 'color 0.2s ease, padding-left 0.2s ease',
    display: 'inline-block',
    '&:hover': {
      color: '#ffffff',
      paddingLeft: 4,
    },
  },
  socialContainer: {
    display: 'flex',
    gap: "8px",
    marginTop: 8,
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'flex-start',
    },
  },
  socialButton: {
    width: 36,
    height: 36,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.2)',
      transform: 'translateY(-2px)',
    },
    '& svg': {
      fontSize: 18,
      color: '#ffffff',
    },
  },
  divider: {
    height: 1,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    margin: '32px 0 20px',
    [theme.breakpoints.down('sm')]: {
      margin: '24px 0 16px',
    },
  },
  bottomSection: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: "12px",
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      textAlign: 'center',
    },
  },
  copyright: {
    fontSize: '0.75rem',
    color: 'rgba(255, 255, 255, 0.6)',
  },
  tagline: {
    fontSize: '0.75rem',
    color: 'rgba(255, 255, 255, 0.5)',
  },
}));

export const Footer = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const companyLinks = [
    { text: 'About Us', href: '/about-us' },
    { text: 'Our Services', href: '/our-services' },
    { text: 'Our Products', href: '/our-products' },
    { text: 'Our Team', href: '/our-team' },
  ];

  const quickLinks = [
    { text: "What's New", href: '/whats-new' },
    { text: 'Contact Us', href: '/get-in-touch' },
    { text: 'Careers', href: '/careers' },
  ];

  const socialLinks = [
    { icon: <LinkedInIcon />, href: 'https://www.linkedin.com/company/arawinz-soft-solutions', label: 'LinkedIn' },
    { icon: <YouTubeIcon />, href: 'https://www.youtube.com/@arawinzsoftsolutions', label: 'YouTube' },
    { icon: <InstagramIcon />, href: 'https://instagram.com/arawinzsoftsolutions/', label: 'Instagram' },
    { icon: <FacebookIcon />, href: 'https://www.facebook.com/Arawinz-Soft-Solutions-100197325544506/', label: 'Facebook' },
  ];

  const handleNavigation = (href) => {
    navigate(href);
  };

  return (
    <Box component="footer" className={classes.footer}>
      <Container maxWidth="lg">
        <Grid container spacing={isMobile ? 2 : 4}>
          {/* Company Info */}
          <Grid item xs={12} sm={6} md={4}>
            <Box className={classes.logoContainer}>
              <img
                src="/static/logo.png"
                alt="Arawinz"
                className={classes.logo}
              />
              <Typography className={classes.companyName}>
                Arawinz Soft Solutions
              </Typography>
            </Box>
            <Typography className={classes.description}>
              Transforming ideas into powerful digital solutions. Your trusted partner for innovative IT services.
            </Typography>
            <Box className={classes.contactItem}>
              <EmailIcon />
              <Typography className={classes.contactText}>
                contact@arawinz.com
              </Typography>
            </Box>
            <Box className={classes.contactItem}>
              <LocationOnIcon />
              <Typography className={classes.contactText}>
                Gachibowli, Hyderabad, India
              </Typography>
            </Box>
          </Grid>

          {/* Company Links */}
          <Grid item xs={6} sm={3} md={2}>
            <Typography className={classes.sectionTitle}>
              Company
            </Typography>
            {companyLinks.map((link, index) => (
              <Box key={index} className={classes.linkItem}>
                <span
                  className={classes.link}
                  onClick={() => handleNavigation(link.href)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && handleNavigation(link.href)}
                >
                  {link.text}
                </span>
              </Box>
            ))}
          </Grid>

          {/* Quick Links */}
          <Grid item xs={6} sm={3} md={2}>
            <Typography className={classes.sectionTitle}>
              Quick Links
            </Typography>
            {quickLinks.map((link, index) => (
              <Box key={index} className={classes.linkItem}>
                <span
                  className={classes.link}
                  onClick={() => handleNavigation(link.href)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && handleNavigation(link.href)}
                >
                  {link.text}
                </span>
              </Box>
            ))}
          </Grid>

          {/* Social Links */}
          <Grid item xs={12} sm={12} md={4}>
            <Typography className={classes.sectionTitle}>
              Follow Us
            </Typography>
            <Box className={classes.socialContainer}>
              {socialLinks.map((social, index) => (
                <IconButton
                  key={index}
                  className={classes.socialButton}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                >
                  {social.icon}
                </IconButton>
              ))}
            </Box>
          </Grid>
        </Grid>

        {/* Divider */}
        <Box className={classes.divider} />

        {/* Bottom Section */}
        <Box className={classes.bottomSection}>
          <Typography className={classes.copyright}>
            © {new Date().getFullYear()} Arawinz Soft Solutions Pvt Ltd. All rights reserved.
          </Typography>
          <Typography className={classes.tagline}>
            Est. 2018 • Crafting Digital Excellence
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};
